import {
  Button,
  ButtonColor,
  ButtonSize,
  Combobox,
  Option,
  TextInput,
} from '@code/innovance-react-ui-kit';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Controller, FieldError, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { LoanDocument, LoanDocumentPageValidation } from 'src/front/api/apiInstance';
import { LoanDocumentPageValidationComponentsResponse } from 'src/front/api/generated';

import useYupResolver from 'src/front/common/hooks/useYupResolver';
import { isIncorrectInnNumber } from 'src/front/common/utils/errorUtils';

import {
  formatAlphaNumericCyrillicPlusDashAndBlank,
  formatFreeText,
  formatMaskedText,
  formatNumeric,
} from 'src/front/common/utils/formUtils';
import { LoanIdentitySchema } from 'src/front/common/utils/validationSchemas';
import Datepicker from 'src/front/components/Datepicker';
import FormModal from 'src/front/components/FormModal';

import {
  ButtonRow,
  FormElement,
  FormRow,
  ResetButton,
  Separator,
  Subtitle,
  Title,
} from 'src/front/components/FormStyles';
import { EXPIRE_DATE_RANGE, MODAL_VIEW } from 'src/front/constant';

const FORMATTER = {
  NUMERIC: formatNumeric,
  ALPHANUMERIC: formatAlphaNumericCyrillicPlusDashAndBlank,
  FREETEXT: formatFreeText,
};

export interface IdentityFormData {
  identityType: Option | null;
  identityDocumentSerialCode: string;
  identityDocumentSerialNumber: string;
  issueDate: Date | null;
  expireDate: Date | null;
  issuedBy: string;
  unitCode: string;
  id?: string;
}

const defaultValues = {
  identityType: null,
  identityDocumentSerialCode: '',
  identityDocumentSerialNumber: '',
  issueDate: null,
  expireDate: null,
  issuedBy: '',
  unitCode: '',
};

interface Props {
  identityTypes: Option[];
  onClose: () => void;
  submit: (formData: IdentityFormData) => void;
  formValues?: IdentityFormData;
}

const IdentityModal: FC<Props> = ({ formValues, identityTypes, onClose, submit }) => {
  const { t } = useTranslation();

  const [formValidations, setFormValidations] = useState<
    LoanDocumentPageValidationComponentsResponse
  >();
  const resolver = useYupResolver(
    LoanIdentitySchema,
    {
      todayMaxControl: true,
      todayMinControl: true,
    },
    { formValidations }
  );

  const {
    watch,
    register,
    errors,
    clearErrors,
    setValue,
    control,
    handleSubmit,
    formState,
    setError,
  } = useForm<IdentityFormData>({
    defaultValues: formValues || defaultValues,
    resolver,
  });

  useEffect(() => {
    register({ name: 'identityDocumentSerialCode' });
    register({ name: 'identityDocumentSerialNumber' });
    register({ name: 'issueDate' });
    register({ name: 'expireDate' });
    register({ name: 'issuedBy' });
    register({ name: 'unitCode' });
  }, [register]);

  const getFormValidations = useCallback(
    async (identityType: string): Promise<void> => {
      const { data } = await LoanDocument.getLoanDocumentPageComponentsValidation(identityType);
      if (data.result === 'OK') {
        [
          ...(data.series.enabled ? [] : [{ name: 'identityDocumentSerialCode', value: '' }]),
          ...(data.documentNumber.enabled
            ? []
            : [{ name: 'identityDocumentSerialNumber', value: '' }]),
          ...(data.issueDate.enabled ? [] : [{ name: 'issueDate', value: null }]),
          ...(data.expireDate?.enabled ? [] : [{ name: 'expireDate', value: null }]),
          ...(data.issuedBy.enabled ? [] : [{ name: 'issuedBy', value: '' }]),
          ...(data.unitCode.enabled ? [] : [{ name: 'unitCode', value: '' }]),
        ].forEach(({ name, value }) => setValue(name, value as {} | undefined));
        setFormValidations(data);
      }
    },
    [setValue]
  );

  const handleIdentityTypeChange = useCallback(
    (callback) => (type: Option | null): void => {
      clearErrors();
      callback(type);
      getFormValidations(type?.value || '');
      setValue('unitCode', '');
    },
    [clearErrors, getFormValidations, setValue]
  );

  useEffect(() => {
    if (formValues?.identityType?.value) {
      getFormValidations(formValues?.identityType?.value);
    }
  }, [formValues, getFormValidations]);

  const handleDateChange = (fieldName: 'issueDate' | 'expireDate') => (date: Date): void =>
    setValue(fieldName, date, {
      shouldValidate: formState.isSubmitted && !!date,
      shouldDirty: formState.isSubmitted || !!date,
    });

  const handleTextChange = (fieldName: string, mask?: string) => (text: string) =>
    setValue(fieldName, formatMaskedText(text, mask), {
      shouldValidate: formState.isSubmitted && !!text,
      shouldDirty: formState.isSubmitted || !!text,
    });

  const onReset = (): void => {
    [
      { name: 'identityType', value: (formValues || defaultValues).identityType },
      {
        name: 'identityDocumentSerialCode',
        value: (formValues || defaultValues).identityDocumentSerialCode,
      },
      {
        name: 'identityDocumentSerialNumber',
        value: (formValues || defaultValues).identityDocumentSerialNumber,
      },
      { name: 'issueDate', value: (formValues || defaultValues).issueDate },
      { name: 'expireDate', value: (formValues || defaultValues).expireDate },
      { name: 'issuedBy', value: (formValues || defaultValues).issuedBy },
      { name: 'unitCode', value: (formValues || defaultValues).unitCode },
    ].forEach(({ name, value }) => setValue(name, value as {} | undefined));
    formValues
      ? getFormValidations(formValues.identityType?.value || '')
      : setFormValidations(undefined);
  };
  const onSubmit = async (formData: IdentityFormData): Promise<void> => {
    const { identityType, identityDocumentSerialNumber } = formData;
    try {
      if (identityType && identityDocumentSerialNumber) {
        const { data } = await LoanDocumentPageValidation.validateLoanAddIdentityData({
          documentType: identityType.value,
          identityDocumentSerialNumber,
        });
        if (data.result === 'OK') {
          await submit({ ...formValues, ...formData });
        }
      }
    } catch (error) {
      if (isIncorrectInnNumber(error)) {
        setError('identityDocumentSerialNumber', {
          type: 'pattern',
          message: error?.errorMessage,
        });
      }
    }
  };

  return (
    <FormModal onClose={onClose} size={MODAL_VIEW.MIDDLE} zIndex={900}>
      <Title>
        <Trans i18nKey="loanApp_documentEntry_documentEntry_title" />
      </Title>
      <Subtitle>
        <Trans i18nKey="cardEntry_documentEntry_table1_subtitle" />
      </Subtitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="identityType"
              render={({ onChange, value }) => (
                <Combobox
                  errorMessage={t((errors?.identityType as FieldError)?.message as string)}
                  items={identityTypes}
                  label={t('general_documentType_label')}
                  name="identityType"
                  onChange={handleIdentityTypeChange(onChange)}
                  searchPlaceholder={t('cardEntry_documentEntry_searchDocumentType_text')}
                  value={value}
                  searchable
                />
              )}
            />
          </FormElement>
        </FormRow>
        {formValidations && watch('identityType') ? (
          <>
            <FormRow>
              <FormElement>
                <TextInput
                  disabled={!formValidations.series.enabled}
                  errorMessage={t(errors?.identityDocumentSerialCode?.message as string, {
                    min: formValidations.series.minimumLength,
                  })}
                  formatter={FORMATTER[formValidations.series.dataType || 'FREETEXT']}
                  label={t('general_documentSeries_label')}
                  maxLength={
                    formValidations.series.maskFormat?.length ||
                    formValidations.series.maximumLength
                  }
                  name="identityDocumentSerialCode"
                  onChange={handleTextChange(
                    'identityDocumentSerialCode',
                    formValidations.series.maskFormat
                  )}
                  required={formValidations.series.required}
                  value={watch('identityDocumentSerialCode')}
                />
              </FormElement>
              <FormElement>
                <TextInput
                  disabled={!formValidations.documentNumber.enabled}
                  errorMessage={t(errors?.identityDocumentSerialNumber?.message as string, {
                    min: formValidations.documentNumber.minimumLength,
                  })}
                  formatter={FORMATTER[formValidations.documentNumber.dataType || 'FREETEXT']}
                  label={t('general_documentNumber_label')}
                  maxLength={
                    formValidations.documentNumber.maskFormat?.length ||
                    formValidations.documentNumber.maximumLength
                  }
                  name="identityDocumentSerialNumber"
                  onChange={handleTextChange(
                    'identityDocumentSerialNumber',
                    formValidations.documentNumber.maskFormat
                  )}
                  required={formValidations.documentNumber.required}
                  value={watch('identityDocumentSerialNumber')}
                />
              </FormElement>
            </FormRow>
            <FormRow>
              <FormElement>
                <Datepicker
                  disabled={!formValidations.issueDate.enabled}
                  errorMessage={t(errors?.issueDate?.message as string)}
                  inputLabel={`${t('general_issueDate_label')}${
                    formValidations.issueDate.required ? ' *' : ''
                  }`}
                  onChange={handleDateChange('issueDate')}
                  value={watch('issueDate') as Date}
                />
              </FormElement>
              <FormElement>
                <Datepicker
                  disabled={!formValidations.expireDate?.enabled}
                  errorMessage={t(errors?.expireDate?.message as string)}
                  inputLabel={`${t('general_expiredDate_label')}${
                    formValidations.expireDate?.required ? ' *' : ''
                  }`}
                  onChange={handleDateChange('expireDate')}
                  rangeYear={EXPIRE_DATE_RANGE}
                  value={watch('expireDate') as Date}
                />
              </FormElement>
            </FormRow>
            <FormRow>
              <FormElement>
                <TextInput
                  disabled={!formValidations.issuedBy.enabled}
                  errorMessage={t(errors?.issuedBy?.message as string, {
                    min: formValidations.issuedBy.minimumLength,
                  })}
                  formatter={FORMATTER[formValidations.issuedBy.dataType || 'FREETEXT']}
                  label={t('general_issuedBy_label')}
                  maxLength={
                    formValidations.issuedBy.maskFormat?.length ||
                    formValidations.issuedBy.maximumLength
                  }
                  name="issuedBy"
                  onChange={handleTextChange('issuedBy', formValidations.issuedBy.maskFormat)}
                  required={formValidations.issuedBy.required}
                  value={watch('issuedBy')}
                />
              </FormElement>
              <FormElement>
                <TextInput
                  disabled={!formValidations.unitCode.enabled}
                  errorMessage={t(errors?.unitCode?.message as string, {
                    min: formValidations.unitCode.minimumLength,
                  })}
                  formatter={FORMATTER[formValidations.unitCode.dataType || 'FREETEXT']}
                  label={t('cardEntry_documentEntry_unitCode_label')}
                  maxLength={
                    formValidations.unitCode.maskFormat?.length ||
                    formValidations.unitCode.maximumLength
                  }
                  name="unitCode"
                  onChange={handleTextChange('unitCode', formValidations.unitCode.maskFormat)}
                  required={formValidations.unitCode.required}
                  value={watch('unitCode')}
                />
              </FormElement>
            </FormRow>
            <Separator />
            <ButtonRow>
              {formState.isDirty ? (
                <ResetButton
                  color={ButtonColor.Secondary}
                  onClick={onReset}
                  size={ButtonSize.Medium}
                  text={t('general_components_reset_button')}
                  type="reset"
                />
              ) : null}
              <Button
                color={ButtonColor.Primary}
                disabled={formState.isSubmitting}
                size={ButtonSize.Medium}
                text={t('general_save_button')}
                type="submit"
              />
            </ButtonRow>
          </>
        ) : null}
      </form>
    </FormModal>
  );
};

export default IdentityModal;
